<template>
    <MessageCard message="Complete the information below and hit the 'Submit' button in the lower right hand corner."
        type="primary" iconClass="iatse-icon-alarm" classes="info_card_full" />

    <LoaderComponent v-if="loading" />
    <div v-else class="plan-content">
        <!-- Form -->
        <form id="missing-contribution-form" class="form missing-contribution" method="post" @submit.prevent>
            <div class="inputs-wrapper align-items-end">
                <div class="custom-select links">
                    <label class="body-text"> Employer/Production <span class="text-danger"> * </span></label>
                    <Multiselect v-model="filters.Employers" :options="employers" placeholder="Type to search"
                        :multiple="true" :hide-selected="true" :searchable="true" :close-on-select="true"
                        @select="handleEmployerChange" label="OrgName" track-by="OrganizationID"
                        @search-change="handleEmployersSearch" :loading="loadingEmployers" @open="handleOpen">
                    </Multiselect>
                    <small v-if="errors.Employers?.length" class="text-secondary-red"> {{ errors.Employers.join('. ') }}
                    </small>
                </div>
                <TextInput type="text" name="mixed" label="Member Name or SSN" placeholder="Enter Member Name or SSN"
                    :max=12 v-model:modelValue="filters.MemberNameSSN" :readonly="true" />
                <DateInput label="Work Start Date" :is-required="true" v-model:modelValue="filters.WorkPeriod.StartDate"
                    :error="errors['WorkPeriod.StartDate']" />
                <DateInput label="Work End Date" :is-required="true" v-model:modelValue="filters.WorkPeriod.EndDate"
                    :error="errors['WorkPeriod.EndDate']" />
                <TextInput type="text" name="mixed" label="Work Location" placeholder="Enter Work Location" :max=50
                    v-model:modelValue="filters.WorkLocation" />
                <SelectComponent label="State\Province" :is-required="true" v-model:selectedOption="filters.State"
                    :options="filteredStates" :single-arrow="true" :error="errors.State" />
            </div>

            <div class="action-buttons pt-5">
                <button type="button" @click.prevent="resetModal" class="btn btn-secondary" :disabled="loadingSubmit">
                    Discard
                </button>
                <button type="submit" class="btn btn-primary" :disabled="loadingSubmit" @click.prevent="handleSubmit">
                    {{ loadingSubmit ? 'Submitting...' : 'Submit' }} </button>
            </div>
        </form>
    </div>
</template>

<script lang="ts">
import { ref, reactive, onMounted, computed, defineComponent } from 'vue';
import { useModalStore } from '@/stores/modal';
import { useProfileStore } from '@/stores/profile';
import { CountryStates, SelectOption } from '@/interfaces/interfaces';
import TextInput from '@/components/form/TextInput.vue';
import DateInput from '@/components/form/DateInput.vue';
import SelectComponent from '@/components/form/SelectComponent.vue';
import Multiselect from 'vue-multiselect';
import axios from 'axios';
import 'vue-multiselect/dist/vue-multiselect.css';
import { useParticipantStore } from '@/stores/participant';

interface Filters {
    WorkPeriod: {
        StartDate: string,
        EndDate: string,
    };
    Employers: Employer[] | null;
    ParticipantID: string;
    MemberNameSSN: string;
    WorkLocation: string;
    State: SelectOption;
}

interface Employer {
    OrganizationID: string | number;
    OrgName: string;
}

export default defineComponent({
    components: {
        TextInput,
        DateInput,
        SelectComponent,
        Multiselect
    },
    props: {
        fund: {
            type: String,
            default: null,
        }
    },
    setup(props, { emit }) {
        const modalStore = useModalStore();
        const profileStore = useProfileStore();
        const errors = ref<string[]>([]);
        const success = ref(null);
        const info = ref('');
        const loading = ref(true);
        const loadingEmployers = ref(false);
        const loadingProductions = ref(false);
        const loadingSubmit = ref(false);
        const allOption = { OrganizationID: 0, OrgName: 'ALL' };
        const initialFilters = {
            WorkPeriod: {
                StartDate: '',
                EndDate: '',
            },
            Employers: [allOption],
            ParticipantID: useParticipantStore().general?.ParticipantID,
            MemberNameSSN: useParticipantStore().general?.SSN,
            WorkLocation: '',
            State: { key: 0, value: 'Select State' },
        }
        const filters: Filters = reactive(JSON.parse(JSON.stringify(initialFilters)));
        const employers = ref<Employer[]>([]);
        const hasEmployers = computed(() => filters.Employers?.some(employer => employer.OrganizationID !== 0));

        const handleOpen = () => {
            employers.value = [allOption];
        }

        // Handle employers search
        const handleEmployersSearch = async (query: string) => {
            loadingEmployers.value = true;

            if (query.length >= 3) {
                try {
                    const [employersResponse] = await Promise.all([
                        axios.get(`/api/organizations-types?query=${query}`),
                        // axios.get(`/api/participant/productions?searchTerm=${query}`)
                    ]);

                    // Combine the results
                    employers.value = [
                        ...employersResponse.data,
                        // ...productionsResponse.data
                    ];
                }
                catch (error) {
                    console.error('Error searching employer:', error);
                }
            }
            loadingEmployers.value = false;
        };

        // Fetch Data
        const fetchData = async () => {
            try {
                if (!profileStore.countries || !profileStore.statesByCountry) {
                    await profileStore.fetchCountriesStates();
                }

                loading.value = false;
            }
            catch (error) {
                console.error('Error:', error);
            }
        }

        // Filter states based on selected country
        const filteredStates = computed(() => {
            const selectedCountry = 'US';
            const states = profileStore.statesByCountry;
            const countryStates = states ? (states as CountryStates[]).filter(state => state.CountryKey === selectedCountry) : [];

            return (countryStates?.map(state => state.States).flat());
        });

        const resetModal = () => {
            Object.assign(filters, JSON.parse(JSON.stringify(initialFilters)));
            errors.value = [];
        }

        const handleEmployerChange = (newSelection: Employer) => {
            if (newSelection.OrganizationID === 0) {
                filters.Employers = [allOption];
            }
            else {
                const filteredTypes: Employer[] = JSON.parse(JSON.stringify(filters.Employers)).filter((type: Employer) => type.OrganizationID !== 0)
                filters.Employers = [...filteredTypes];
            }
        }

        const handleSubmit = async () => {
            loadingSubmit.value = true;
            const formData = { ...filters, State: filters?.State?.value };
            errors.value = []

            if (!hasEmployers.value) {
                formData.Employers = null;
            }

            try {
                const response = await axios.post('api/participant/missing-contributions', formData);

                success.value = response.data.success;

                if (success.value) {
                    useModalStore().openModal('success-modal', null, 'modal-success', null, {
                        title: "Successful",
                        content: {
                            type: "text",
                            value: success.value,
                            icon: 'iatse-icon-user-verified'
                        },
                        onConfirmButton: 'Ok',
                        onConfirm: () => resetModal()
                    })
                }
            } catch (error) {
                if (axios.isAxiosError(error) && error.response?.data?.errors) {
                    errors.value = error.response.data.errors;
                } else {
                    errors.value = ['Something went wrong. Please try again.'];
                    console.error(error);
                }
            } finally {
                loadingSubmit.value = false;
            }
        }

        onMounted(async () => await fetchData());

        return {
            loading,
            loadingSubmit,
            loadingEmployers,
            loadingProductions,
            errors,
            success,
            filters,
            employers,
            hasEmployers,
            filteredStates,
            handleOpen,
            handleEmployerChange,
            handleEmployersSearch,
            handleSubmit,
            resetModal
        };
    }
});
</script>
